import {
  Alert,
  Button,
  Chip,
  Modal,
  SegmentedFilter,
  Stack,
  Text,
} from "@assetwatch/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getMarketplaceIcon } from "../../lib/marketplaceFormat";
import { toExpansionRequest } from "../../lib/marketplaceRequests";
import type {
  MarketplaceOutcomeFilter,
  MarketplaceResponse,
  SelectedMarketplaceRequest,
} from "../../types/marketplace";
import { AvailableCapabilityCard } from "./AvailableCapabilityCard";
import { MarketplaceCard } from "./MarketplaceCard";

type AllSubscriptionsModalProps = {
  marketplace: MarketplaceResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (request: SelectedMarketplaceRequest) => void;
};

function useOutcomeFilterItems() {
  const { t } = useTranslation();

  return [
    { value: "all", label: t("marketplace.outcomes.all") },
    {
      value: "reduce-unplanned-downtime",
      label: t("marketplace.outcomes.reduceUnplannedDowntime"),
    },
    {
      value: "extend-asset-life",
      label: t("marketplace.outcomes.extendAssetLife"),
    },
    {
      value: "lower-maintenance-costs",
      label: t("marketplace.outcomes.lowerMaintenanceCosts"),
    },
    {
      value: "accelerate-work-order-resolution",
      label: t("marketplace.outcomes.accelerateWorkOrderResolution"),
    },
    {
      value: "enhance-sustainability-energy-efficiency",
      label: t("marketplace.outcomes.enhanceSustainabilityEnergyEfficiency"),
    },
  ] satisfies { value: MarketplaceOutcomeFilter; label: string }[];
}

export function AllSubscriptionsModal({
  marketplace,
  open,
  onOpenChange,
  onSelect,
}: AllSubscriptionsModalProps) {
  const { t } = useTranslation();
  const outcomeFilterItems = useOutcomeFilterItems();
  const [selectedOutcome, setSelectedOutcome] =
    useState<MarketplaceOutcomeFilter>("all");
  const matchesOutcome = (outcomes: string[]) =>
    selectedOutcome === "all" || outcomes.includes(selectedOutcome);
  const subscriptions = marketplace.activeSubscriptions.filter((subscription) =>
    matchesOutcome(subscription.outcomes),
  );
  const capabilities = marketplace.availableCapabilities.filter((capability) =>
    matchesOutcome(capability.outcomes),
  );
  const shownCount = subscriptions.length + capabilities.length;
  const totalCount =
    marketplace.activeSubscriptions.length +
    marketplace.availableCapabilities.length;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("marketplace.allSubscriptionsTitle")}
      description={t("marketplace.allSubscriptionsDescription")}
      size="lg"
      footer={
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          {t("common.close")}
        </Button>
      }
    >
      <Stack gap={16} fullWidth>
        <SegmentedFilter
          aria-label={t("marketplace.outcomeFilterLabel")}
          value={selectedOutcome}
          onValueChange={(value) =>
            setSelectedOutcome(value as MarketplaceOutcomeFilter)
          }
          items={outcomeFilterItems}
        />
        {shownCount === 0 ? (
          <Alert
            tone="informative"
            title={t("marketplace.noOutcomeMatchesTitle")}
            subtext={t("marketplace.noOutcomeMatchesDescription")}
          />
        ) : (
          <div className="grid gap-(--spacing-16) md:grid-cols-2 lg:grid-cols-3">
            {subscriptions.map((subscription) => (
              <MarketplaceCard
                key={`active-${subscription.id}`}
                title={subscription.name}
                type={subscription.type}
                description={subscription.description}
                icon={getMarketplaceIcon(subscription.type, subscription.name)}
                headerAccessory={
                  <Chip
                    tone="positive"
                    icon="circle-check"
                    label={t("marketplace.active")}
                  />
                }
                onClick={() => onSelect(toExpansionRequest(subscription))}
              />
            ))}
            {capabilities.map((capability) => (
              <AvailableCapabilityCard
                key={`future-${capability.id}`}
                capability={capability}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
        <Text tone="subtle">
          {t("marketplace.subscriptionCountShown", {
            shown: shownCount,
            total: totalCount,
          })}
        </Text>
      </Stack>
    </Modal>
  );
}
